/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { useCanAccessEntityScreening } from '@/hooks/useCanAccessEntityScreening';
import { useFeatures } from '@/hooks/useFeatures';
import { ConnectCompanyDialog } from './-components/ConnectCompanyDialog';
import { EntityScreeningTable } from './-components/EntityScreeningTable';
import { FlagConfirmationDialog } from './-components/FlagConfirmationDialog';
import { useEntityScreeningController } from './-hooks/useEntityScreeningController';

const entityScreeningSearch = z.object({
  page: z.number().int().min(1).catch(1),
  limit: z.number().int().min(10).max(500).catch(20),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.array(z.string())),
    ])
    .optional(),
  tab: z.string().optional(),
});

export const Route = createFileRoute('/(client)/entity-screening/')({
  validateSearch: entityScreeningSearch,
  beforeLoad: ({ context }) => {
    const features = (context as { features?: Record<string, boolean> })
      ?.features;
    if (features && features['entity-screening-v2'] === false) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: EntityScreeningPage,
});

function EntityScreeningPage() {
  const navigate = useNavigate();
  const { isEntityScreeningV2Enabled } = useFeatures();
  const canAccess = useCanAccessEntityScreening();
  const ctrl = useEntityScreeningController();

  useEffect(() => {
    if (!canAccess) navigate({ to: '/dashboard' });
  }, [canAccess, navigate]);

  if (!isEntityScreeningV2Enabled || !canAccess) return null;

  return (
    <PageContainer>
      <EntityScreeningTable
        data={ctrl.items}
        isFetching={ctrl.isFetching}
        totalCount={ctrl.meta?.totalCount ?? 0}
        table={ctrl.table}
        rowSelection={ctrl.rowSelection}
        onRowSelectionChange={ctrl.setRowSelection}
        quickFilter={ctrl.quickFilter}
        onQuickFilterChange={ctrl.setQuickFilter}
        onDismiss={ctrl.handleDismiss}
        onRequestFlag={ctrl.requestFlag}
        onConnect={ctrl.openConnectDialog}
        isValidating={ctrl.isValidating}
        countryOptions={ctrl.countryOptions}
        facilityProcessOptions={ctrl.facilityProcessOptions}
        threshold={ctrl.threshold}
        isThresholdLoading={ctrl.isThresholdLoading}
        isUpdatingThreshold={ctrl.isUpdatingThreshold}
        onUpdateThreshold={ctrl.handleUpdateThreshold}
      />

      <FlagConfirmationDialog
        open={ctrl.flagDialogOpen}
        onOpenChange={ctrl.setFlagDialogOpen}
        onConfirm={ctrl.confirmFlag}
        count={ctrl.pendingFlagIds.length}
      />

      <ConnectCompanyDialog
        open={ctrl.connectDialog.open}
        onOpenChange={ctrl.setConnectDialogOpen}
        companyName={ctrl.connectDialog.companyName}
        onConfirm={ctrl.confirmConnect}
        isLoading={ctrl.isConnecting}
      />
    </PageContainer>
  );
}
